import { localizedAccountDisplayName, uniqueDisplayAccount } from "../utils/localizedAccountDisplayName";
// src/pages/EditPaymentMethod.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { apiGet, apiPut } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

type Account = {
  id: number;
  name: string;
  type: string;
  initial_balance?: number;
  kind?: string;
  is_system?: boolean;
};

type PaymentMethodRead = {
  name: string;
  // FEE-RATE-SEMANTICS-1 / EDIT-PM-LEGACY-REWRITE-1: consume the truthful
  // derived effective percentage (Phase 1 guarantees it is present and derived
  // through effective_payment_method_rate()). Raw fee_rate is intentionally NOT
  // read here -- reading it re-introduces the legacy >1 unit ambiguity this
  // containment removes (e.g. a stored 3.0 meaning 3% would display as 300).
  fee_rate_percent: number | null;
  fixed_fee: number | null;
  account_id: number | null;

  provider?: string | null;
  provider_method?: string | null;
  online?: boolean | null;
};

function toNum(str: string, fallback = 0) {
  const n = Number(str);
  return Number.isFinite(n) ? n : fallback;
}
function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export default function EditPaymentMethod() {
  const { t } = useTranslation();
  const { company_id, id } = useParams<{ company_id: string; id: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    // Internal field carries PERCENT units explicitly (e.g. "1.5" == 1.5%).
    fee_rate_percent: "0",
    fixed_fee: "0",
    account_id: "",
  });

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ raw: string } | { fallback: true } | null>(null);

  // Stripe meta
  const [meta, setMeta] = useState<{
    provider?: string | null;
    provider_method?: string | null;
    online?: boolean | null;
  }>({});

  // Root B — ordinary user-selectable payment target: non-system asset account
  // whose normalized (casing/whitespace) kind is cash or bank. Payment/gateway
  // integration kinds are intentionally NOT offered as ordinary new selections.
  // The currently-selected account (which may be legacy-invalid or a gateway) is
  // shown via `selectedAccountObj` below but is not added to this option list,
  // so it remains visible and correctable without being silently cleared.
  const filteredAccounts = useMemo(() => {
    const norm = (s?: string | null) => (s ?? "").toLowerCase().trim();
    return (accounts || []).filter((a) => {
      const t = norm(a.type);
      const k = norm(a.kind);
      return a.is_system !== true && t === "asset" && (k === "cash" || k === "bank");
    });
  }, [accounts]);

  useEffect(() => {
    if (!id) return;

    // Load this payment method
    apiGet<PaymentMethodRead>(`/payment-methods/${id}`).then((res) => {
      setForm({
        name: res.name ?? "",
        // Prefill directly from the derived effective percent -- no *100. Keep
        // up to four decimal places (matching the Phase 1 percent contract) and
        // drop insignificant trailing zeros; zero/null display as "0".
        fee_rate_percent:
          res.fee_rate_percent != null
            ? Number(res.fee_rate_percent).toFixed(4).replace(/\.?0+$/, "")
            : "0",
        fixed_fee: res.fixed_fee != null ? String(res.fixed_fee) : "0",
        account_id: res.account_id != null ? String(res.account_id) : "",
      });
      setMeta({
        provider: res.provider ?? null,
        provider_method: res.provider_method ?? null,
        online: res.online ?? null,
      });
    });

    // Accounts (scoped)
    apiGet<Account[]>(`/accounts?company_id=${company_id}`)
      .then((rows) => setAccounts(Array.isArray(rows) ? rows : []))
      .catch(() => setAccounts([]));
  }, [id, company_id]);

  const isStripe = (meta.provider || "").toLowerCase() === "stripe";

  // Resolve the current selection from the FULL account list (not just the
  // ordinary-eligible options), so a legacy-invalid or gateway account that the
  // method currently points at stays visibly represented and is never silently
  // cleared. Replacing it still requires choosing an ordinary option.
  const selectedAccountObj =
    (accounts || []).find((a) => String(a.id) === form.account_id) || null;

  const labelCls = `block mb-1 ${A.label}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    setError(null);

    const acct = form.account_id?.trim() ? Number(form.account_id) : null;

    const payload: any = {
      name: form.name.trim(),
      account_id: acct,
    };

    // Only allow editing fees for NON-Stripe methods.
    // EDIT-PM-LEGACY-REWRITE-1: submit through the explicit percent channel
    // (fee_rate_percent, 0..100, <=4dp per the Phase 1 backend contract) and
    // never a bare fee_rate key. Explicit 0 is a valid reset. The backend
    // normalizes percent -> canonical fraction, so a legacy >1 row is stored
    // canonically on save instead of being silently clamped to 100%.
    if (!isStripe) {
      const pct = clamp(toNum(form.fee_rate_percent, 0), 0, 100);
      const fixed = Math.max(0, toNum(form.fixed_fee, 0));

      payload.fee_rate_percent = Number(pct.toFixed(4));
      payload.fixed_fee = Number(fixed.toFixed(2));
    }

    try {
      await apiPut(`/payment-methods/${id}`, payload);
      navigate(companyRoute(company_id!, "/settings/payment-methods"), {
        replace: true,
      });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(detail ? { raw: detail } : { fallback: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4 sm:pt-6`}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-md flex items-center border-b border-gray-100 px-2 pb-2 mb-4">
        <button
          onClick={() => navigate(-1)}
          className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.back")}
          type="button"
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`flex-1 text-center ${A.title}`}>{t("paymentMethods.editTitle")}</h2>

        <button
          onClick={() =>
            navigate(companyRoute(company_id!, "/settings/payment-methods"), {
              replace: true,
            })
          }
          className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.close")}
          type="button"
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Card */}
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md px-2"
        autoComplete="off"
      >
        {/* ✅ keep overflow-visible so AppleComboBox dropdown can render */}
        <div className={`${A.card} p-6 overflow-visible`}>
          {/* Name */}
          <div className="mb-4">
            <label className={labelCls}>{t("directory.common.name")}</label>
            <input
              className={A.inputBase}
              name="name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              maxLength={40}
              disabled={saving}
            />
          </div>

          {/* Fees */}
          {!isStripe ? (
            <>
              <div className="mb-4">
                <label className={labelCls}>{t("paymentMethods.feeRate")}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  inputMode="decimal"
                  className={A.inputBase}
                  value={form.fee_rate_percent}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, fee_rate_percent: e.target.value }))
                  }
                  placeholder={t("paymentMethods.percentExample")}
                  disabled={saving}
                />
              </div>

              <div className="mb-4">
                <label className={labelCls}>{t("paymentMethods.fixedFee")}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  className={A.inputBase}
                  value={form.fixed_fee}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, fixed_fee: e.target.value }))
                  }
                  placeholder={t("paymentMethods.fixedExample")}
                  disabled={saving}
                />
              </div>
            </>
          ) : (
            <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500">
              {t("paymentMethods.stripeFeeNotice")}
            </div>
          )}

          {/* Receiving Account */}
          <div className="mb-5">
            <label className={labelCls}>{t("paymentMethods.receivingAccount")}</label>
            <AppleComboBox
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              options={filteredAccounts}
              value={selectedAccountObj}
              onChange={(opt: Account | null) =>
                setForm((f) => ({
                  ...f,
                  account_id: opt ? String(opt.id) : "",
                }))
              }
              getOptionSearchText={(opt) => opt ? opt.name : ""}
              getOptionLabel={(opt) => localizedAccountDisplayName(opt ? uniqueDisplayAccount(accounts, opt.id, opt.name) : undefined, t, opt ? opt.name : "")}
              placeholder={t("paymentMethods.selectAccount")}
              // match Apple Standard input styling
              inputProps={{ className: A.inputBase }}
            />
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {"raw" in error ? error.raw : t("purchases.errors.saveChanges")}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            className="w-full justify-center"
            disabled={saving}
          >
            {saving ? t("directory.common.savingDots") : t("bankAccounts.saveChanges")}
          </Button>
        </div>
      </form>
    </div>
  );
}
